'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Gallery } from '../../types/gallery';

import { fetchStoreGalleries } from '@eliadmin/core/src/api/general/general-api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { GallerySkeletonStructure } from './gallery-skeleton-structure';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function GalleriesList({ locale }: { locale: string }) {
  const galleriesQuery = useInfiniteQuery({
    queryKey: ['GALLERIES', locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchStoreGalleries<Gallery>({
        locale,
        query: {
          page: pageParam,
          results: 9,
          images: true,
        },
      });
    },
    getNextPageParam: (lastPage: any, pages) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const { t } = useTranslations();

  const galleries = galleriesQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <section className={cn('custom-section', 'bg-[#F1F6F8] pb-16 pt-8')}>
        {galleriesQuery.error && <p className="pt-8 text-center">{t('text2364')}</p>}
        {galleriesQuery.isLoading && <GallerySkeletonStructure />}
        {galleriesQuery.isSuccess && galleries.length > 0 && (
          <div
            className={cn(
              'custom-container',
              'grid cursor-pointer grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3',
            )}
          >
            {galleries.map((gallery: Gallery, index) => (
              <div
                key={gallery.name}
                onClick={handleOpenGallery([gallery.image, ...gallery.images], index)}
                className="relative flex h-[205px] w-full items-end justify-center overflow-hidden rounded-[16px] sm:h-[232px]"
              >
                <p className="relative z-10 pb-8 text-2xl font-bold uppercase text-white">
                  {gallery.name}
                </p>
                <div
                  className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-gallery-card__overlay')}
                />
                <Image
                  src={gallery?.image?.url}
                  alt={resolveImageAlt(gallery?.image, gallery?.name)}
                  height={0}
                  width={0}
                  className="absolute inset-0 z-0 h-full w-full object-cover object-center"
                  sizes="100vw"
                />
              </div>
            ))}
          </div>
        )}
        <div
          className={cn('mt-8 flex w-full items-center justify-center', {
            hidden: !galleriesQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => galleriesQuery.fetchNextPage()}
            isLoading={galleriesQuery.isFetchingNextPage}
            className="w-full sm:w-fit"
            variant={'dark'}
          >
            {t('text338')}
          </Button>
        </div>
      </section>
    </>
  );
}
